/**
 * @fileoverview O quadro do CRM compartilhado por link externo, para quem não
 * tem login: só leitura. Fica fora dos providers do app logado (App.js): sem
 * sessão, socket, filas nem cobrança, e nenhuma chamada da API interna.
 *
 * O código do link vem depois do "#" no endereço (o navegador não o manda a
 * servidor nenhum) e vai à API num cabeçalho. A situação do link é a que a API
 * diz a cada leitura: senha pedida, revogado, expirado ou inválido trocam a
 * tela na hora, sem mostrar nada do quadro. Atualiza a cada 30 s com a aba à
 * vista (304 quando nada mudou) e na hora em que a aba volta.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import { useTheme } from "@material-ui/core/styles";
import { Eye, RefreshCw, Search, Sun, Moon, LayoutGrid } from "lucide-react";

import KanbanColumn from "../../components/KanbanBoard/KanbanColumn";
import { filterBoardLeads } from "../../components/KanbanBoard/filterBoardLeads";
import ModernSelect from "../../components/ModernSelect";
import ColorModeContext from "../../layout/themeContext";
import { i18n } from "../../translate/i18n";
import { formatDateTime, formatTime } from "../../utils/formatLocaleDate";
import SharedCardDialog from "./SharedCardDialog";
import { BoardSkeleton, PasswordGate, StatusScreen } from "./ShareScreens";
import { denialOf, fetchSharedBoard, readShareToken, unlockSharedBoard } from "./sharedBoardApi";
import { groupByColumn } from "./toKanbanLead";
import useStyles from "./styles";

export { isSharedBoardPath } from "./sharedBoardApi";

const t = (key, options) => i18n.t(`sharedBoard.${key}`, options);

const POLL_MS = 30 * 1000;
const UNASSIGNED = "unassigned";
const noop = () => undefined;

/** Fora dos buscadores e sem Referer, enquanto a página está aberta; o título leva o nome do quadro. */
const usePageMeta = (boardName) => {
  useEffect(() => {
    const robots = document.querySelector('meta[name="robots"]');
    const previousRobots = robots ? robots.getAttribute("content") : null;
    if (robots) robots.setAttribute("content", "noindex, nofollow");
    const referrer = document.createElement("meta");
    referrer.setAttribute("name", "referrer");
    referrer.setAttribute("content", "no-referrer");
    document.head.appendChild(referrer);
    return () => {
      if (robots && previousRobots !== null) robots.setAttribute("content", previousRobots);
      referrer.remove();
    };
  }, []);

  useEffect(() => {
    document.title = `${boardName || t("pageTitle")} · Sttelar Talk`;
  }, [boardName]);
};

const SharedBoard = () => {
  const classes = useStyles();
  const dark = useTheme().palette.type === "dark";
  const colorModeContext = useContext(ColorModeContext);
  const token = useMemo(() => readShareToken(), []);
  const [phase, setPhase] = useState(token ? "loading" : "invalid");
  const [data, setData] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [offline, setOffline] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [assignee, setAssignee] = useState("");
  const [openLead, setOpenLead] = useState(null);
  const etagRef = useRef(null);
  const inFlight = useRef(false);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  usePageMeta(data?.board?.name);

  // O link deixou de abrir: some com tudo o que era do quadro.
  const close = useCallback((next) => {
    setData(null);
    setOpenLead(null);
    etagRef.current = null;
    setPhase(next);
  }, []);

  // Devolve a situação em que a leitura deixou a página ("ready", "password", ...).
  const load = useCallback(
    async ({ manual = false } = {}) => {
      if (!token || inFlight.current) return null;
      inFlight.current = true;
      if (manual) setRefreshing(true);
      try {
        const res = await fetchSharedBoard(token, etagRef.current);
        if (res.status === 200) {
          setData(res.data);
          etagRef.current = res.headers?.etag || null;
        }
        setUpdatedAt(new Date());
        setOffline(false);
        setPhase("ready");
        return "ready";
      } catch (err) {
        const denial = denialOf(err);
        const showing = phaseRef.current === "ready";
        if (denial === "password") {
          // Estava vendo o quadro e a sessão caiu (senha trocada): pede de novo.
          if (showing) setSessionEnded(true);
          close("password");
        } else if (denial === "error" || denial === "rateLimited") {
          // Rede, servidor ou limite: com o quadro na tela, ele fica e a página avisa.
          if (showing) setOffline(true);
          else setPhase(denial);
        } else {
          close(denial);
        }
        return denial;
      } finally {
        inFlight.current = false;
        setRefreshing(false);
      }
    },
    [token, close]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (phase !== "ready") return undefined;
    const refresh = () => {
      if (!document.hidden) load();
    };
    const timer = setInterval(refresh, POLL_MS);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [phase, load]);

  /** A senha: null quando entrou; senão, a chave do erro (sharedBoard.password.*). */
  const unlock = async (password) => {
    try {
      await unlockSharedBoard(token, password);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) return "wrong";
      if (status === 429) return "tooMany";
      const denial = denialOf(err);
      if (denial !== "error") close(denial);
      return "failed";
    }
    setSessionEnded(false);
    etagRef.current = null;
    // A senha passou, mas a sessão não voltou no pedido seguinte: o navegador
    // não guardou o cookie (bloqueado). Avisa, em vez de ficar carregando.
    if ((await load()) === "password") return "cookies";
    return null;
  };

  const retry = () => {
    setPhase("loading");
    load();
  };

  const columns = useMemo(() => data?.columns || [], [data]);
  const boardLeads = useMemo(() => groupByColumn(data?.cards || []), [data]);
  const filteredLeads = useMemo(
    () => filterBoardLeads(boardLeads, search.trim(), assignee),
    [boardLeads, search, assignee]
  );
  const assignees = useMemo(
    () => Array.from(new Set((data?.cards || []).map((card) => card.assignee).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [data]
  );
  const columnOf = (lead) => columns.find((column) => column.key === lead?.statusId) || null;

  // O responsável escolhido saiu do quadro (na releitura): volta a mostrar todos.
  useEffect(() => {
    if (assignee && assignee !== UNASSIGNED && !assignees.includes(assignee)) setAssignee("");
  }, [assignee, assignees]);

  if (phase === "loading") return <BoardSkeleton />;
  if (phase === "password") return <PasswordGate onSubmit={unlock} sessionEnded={sessionEnded} />;
  if (phase !== "ready" || !data) {
    return <StatusScreen kind={phase === "ready" ? "error" : phase} onRetry={phase === "error" || phase === "rateLimited" ? retry : undefined} />;
  }

  const toggleTheme = colorModeContext?.colorMode?.toggleColorMode;

  return (
    <main className={classes.root}>
      <header className={classes.header}>
        <div className={classes.headerLeft}>
          <span className={classes.eyebrow}>
            <span className={classes.brand}>Sttelar Talk</span>
            <span aria-hidden="true">·</span>
            <span>{t("eyebrow")}</span>
          </span>
          <h1 className={classes.title} title={data.board.name}>
            {data.board.name}
          </h1>
        </div>
        <div className={classes.headerRight}>
          <span className={classes.readOnlyBadge} data-read-only-badge>
            <Eye size={15} strokeWidth={1.9} />
            <span className={classes.badgeLong}>{t("readOnly")}</span>
            <span className={classes.badgeShort}>{t("readOnlyShort")}</span>
          </span>
          <div className={classes.actions}>
            <Tooltip title={t("refresh")}>
              <IconButton className={classes.iconBtn} onClick={() => load({ manual: true })} disabled={refreshing} aria-label={t("refresh")}>
                {refreshing ? <CircularProgress size={15} color="inherit" /> : <RefreshCw size={16} strokeWidth={1.75} />}
              </IconButton>
            </Tooltip>
            {toggleTheme && (
              <Tooltip title={dark ? t("themeLight") : t("themeDark")}>
                <IconButton className={classes.iconBtn} onClick={toggleTheme} aria-label={dark ? t("themeLight") : t("themeDark")}>
                  {dark ? <Sun size={16} strokeWidth={1.75} /> : <Moon size={16} strokeWidth={1.75} />}
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>
      </header>

      <div className={classes.meta}>
        {updatedAt && <span>{t("updatedAt", { time: formatTime(updatedAt) })}</span>}
        {data.expiresAt && <span>· {t("validUntil", { date: formatDateTime(data.expiresAt) })}</span>}
        {offline && <span className={classes.warning}>· {t("offline")}</span>}
      </div>

      <div className={classes.toolbar}>
        <div className={classes.searchWrap}>
          <Search size={15} className={classes.searchIcon} strokeWidth={1.75} />
          <TextField
            placeholder={t("search")}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={classes.searchField}
            variant="outlined"
            size="small"
            inputProps={{ "aria-label": t("search") }}
          />
        </div>
        {assignees.length > 0 && (
          <ModernSelect
            className={classes.assigneeSelect}
            size="small"
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            displayEmpty
            inputProps={{ "aria-label": t("allAssignees") }}
          >
            <MenuItem value="">{t("allAssignees")}</MenuItem>
            <MenuItem value={UNASSIGNED}>{t("unassigned")}</MenuItem>
            {assignees.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </ModernSelect>
        )}
      </div>

      {data.truncated && <p className={classes.notice}>{t("truncated", { count: data.cards.length })}</p>}

      <div className={classes.boardWrapper}>
        {columns.length === 0 ? (
          <StatusEmpty />
        ) : (
          <div className={classes.board}>
            {columns.map((column) => (
              <KanbanColumn
                key={column.key}
                status={{ id: column.key, name: column.name, color: column.color, isCompleted: column.isCompleted }}
                leads={filteredLeads[column.key] || []}
                readOnly
                emptyText={t("emptyColumn")}
                onViewLead={setOpenLead}
                onDropCard={noop}
                onAddLead={noop}
              />
            ))}
          </div>
        )}
      </div>

      {openLead && (
        <SharedCardDialog
          token={token}
          lead={openLead}
          column={columnOf(openLead)}
          onClose={() => setOpenLead(null)}
          onDenied={load}
        />
      )}
    </main>
  );
};

const StatusEmpty = () => (
  <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, opacity: 0.8 }}>
    <LayoutGrid size={28} strokeWidth={1.5} />
    <Typography>{t("noColumns")}</Typography>
  </div>
);

export default SharedBoard;
